
import React from 'react';
import { HebrewTitleInput } from './HebrewTitleInput';
import { HebrewDescriptionInput } from './HebrewDescriptionInput';
import { EnglishTranslationField } from './EnglishTranslationField';
import { DaySelector } from './DaySelector';
import { TimeSelector } from './TimeSelector';
import { TaskTemplateSelector, TaskTemplate } from './TaskTemplateSelector';
import { Button } from '../ui/button';
import { useSaveTaskTemplateMutation } from '@/hooks/mutations/useSaveTaskTemplateMutation';
import { useToast } from '@/hooks/use-toast';
import { Coins } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface FormFieldsProps {
  register: any;
  watch: any;
  setValue: any;
  hebrewTitle: string;
  hebrewDescription: string;
}

export const FormFields = ({
  register,
  watch,
  setValue,
  hebrewTitle,
  hebrewDescription,
}: FormFieldsProps) => {
  const { toast } = useToast();
  const saveTemplateMutation = useSaveTaskTemplateMutation();
  
  const handleHebrewTitleChange = (hebrew: string, english: string) => {
    setValue('hebrewTitle', hebrew);
    if (english) setValue('title', english);
  };

  const handleHebrewDescriptionChange = (hebrew: string, english: string) => {
    setValue('hebrewDescription', hebrew);
    if (english) setValue('description', english);
  };

  const handleTemplateSelect = (template: TaskTemplate) => {
    setValue('title', template.title);
    setValue('description', template.description);
    // Clear Hebrew fields when template is selected
    setValue('hebrewTitle', '');
    setValue('hebrewDescription', '');
  };

  const handleSaveAsTemplate = async () => {
    const title = watch('title');
    const description = watch('description');
    
    if (!title) {
      toast({
        title: "Missing Title",
        description: "Please enter a title for the task template",
        variant: "destructive",
      });
      return;
    }

    try {
      await saveTemplateMutation.mutateAsync({
        title,
        description: description || null,
        hebrew_title: hebrewTitle || null,
        hebrew_description: hebrewDescription || null,
      });

      toast({
        title: "Success",
        description: "Task template saved successfully!",
      });
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: "Error",
        description: "Failed to save task template",
        variant: "destructive",
      });
    }
  };
  
  // Prevent scrolling on the coins input
  const preventScroll = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  };

  return (
    <div className="space-y-4">
      <TaskTemplateSelector onSelect={handleTemplateSelect} />

      <HebrewTitleInput
        value={hebrewTitle}
        onChange={handleHebrewTitleChange}
      />

      <EnglishTranslationField
        id="title"
        label="Task Title (English)"
        register={register}
      />

      <HebrewDescriptionInput
        value={hebrewDescription}
        onChange={handleHebrewDescriptionChange}
      />

      <EnglishTranslationField
        id="description"
        label="Description (English)"
        register={register}
        isTextArea
      />

      <div className="space-y-2">
        <Label htmlFor="coins">
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-amber-500" />
            <span>Coins Value</span>
          </div>
        </Label>
        <Input
          id="coins"
          type="number"
          min="0"
          {...register('coins')}
          defaultValue={0}
          className="bg-[#1e293b] text-white"
          onWheel={preventScroll}
        />
      </div>

      <DaySelector register={register} setValue={setValue} watch={watch} />
      <TimeSelector register={register} setValue={setValue} />

      <Button
        type="button"
        variant="outline"
        onClick={handleSaveAsTemplate}
        className="w-full bg-[#1e293b] text-white border-input hover:bg-[#2d3748] hover:text-white"
      >
        Save as Task Type
      </Button>
    </div>
  );
};
